/**
 * Everything a player could ever hold, in an order somebody can find things in.
 *
 * The creative palette reads this. Its whole job is to be **complete**: a
 * palette that quietly leaves something out is worse than no palette at all,
 * because the one thing it is for is reaching the block you cannot otherwise
 * get, and nobody can tell a missing entry from a block that does not exist.
 *
 * So it is built by walking the whole item table rather than by listing what
 * seems useful, and a test walks it the other way round to say that nothing
 * holdable is absent.
 *
 * **Grouped, not sorted.** Ordering by id would put сало between a door and a
 * pickaxe. The groups below are the ones a person actually thinks in - what
 * the ground is made of, what a house is made of, what you carry - and inside
 * a group the id order is kept, which is roughly the order things were added
 * and therefore roughly the order they are unlocked.
 */

import {
  BLOCKS, blockDef, isDoor, isShutter, isBed, isStove, isChest, isRushnyk,
  isWall, isBlueprint, blueprintBase,
} from "./registry.js";
import type { UiKey } from "../text/lang.js";
import { spawnForItem } from "../entity/spawn.js";
import {
  PLAIN_ITEMS, FIRST_NON_BLOCK_ITEM, itemDef, isPlaceable, placedBlock,
  itemTool, itemArmour, itemUse, type ItemId,
} from "./items.js";

export type PaletteGroup =
  | "terrain" | "building" | "plants" | "creatures"
  | "tools" | "armour" | "food" | "other";

/**
 * The heading over each shelf, as a key into the interface's own words.
 *
 * A key rather than the text, so a shelf cannot end up named in Ukrainian in
 * an English game - the words themselves live in core/text/lang.ts with both
 * halves required.
 */
export const GROUP_LABELS: Readonly<Record<PaletteGroup, UiKey>> = {
  terrain: "shelf-terrain",
  building: "shelf-building",
  plants: "shelf-plants",
  creatures: "shelf-creatures",
  tools: "shelf-tools",
  armour: "shelf-armour",
  food: "shelf-food",
  other: "shelf-other",
};

/** The groups in the order they are shown. */
export const GROUP_ORDER: readonly PaletteGroup[] = [
  "terrain", "building", "plants", "creatures",
  "tools", "armour", "food", "other",
];

export interface PaletteEntry {
  readonly id: ItemId;
  readonly group: PaletteGroup;
}

/**
 * Which shelf a thing belongs on.
 *
 * Asked of the item rather than written into a table beside it, so a new
 * block lands somewhere sensible the day it is added instead of the day
 * somebody remembers to add it here.
 */
function groupOf(id: ItemId): PaletteGroup {
  // Asked first: a creature item is a tool by no other measure, and its
  // `use` would otherwise land it among the hoes and the fishing rod.
  if (spawnForItem(id) !== undefined) return "creatures";
  if (itemArmour(id) !== undefined) return "armour";
  if (itemTool(id) !== undefined) return "tools";
  const use = itemUse(id);
  if (use?.kind === "eat") return "food";
  if (use?.kind === "plant" || use?.kind === "fish" || use?.kind === "shoot") return "tools";


  if (id < FIRST_NON_BLOCK_ITEM) {
    const def = blockDef(id);
    if (def.render === "cross") return "plants";
    if (isDoor(id) || isShutter(id) || isBed(id) || isStove(id)) return "building";
    // What the ground is made of: solid, opaque, and it grows there.
    const terrain = [
      "grass", "dirt", "stone", "chornozem", "sand", "clay", "granite", "peat",
      "coal_ore", "iron_ore", "manganese_ore", "graphite_ore", "oil_shale",
      "ilmenite", "rutile", "water",
    ];
    if (terrain.includes(def.name)) return "terrain";
    if (def.name.endsWith("_log") || def.name.endsWith("_leaves")) return "plants";
    return "building";
  }
  return "other";
}

/**
 * Every holdable thing, grouped.
 *
 * A block whose id is at or above FIRST_NON_BLOCK_ITEM has no item at all -
 * flowing water, the second half of a door - and is left out, because there
 * is nothing to hand anybody.
 */
export function palette(): readonly PaletteEntry[] {
  const out: PaletteEntry[] = [];
  const seen = new Set<number>();

  // A block that some plain item already puts down does not need a shelf of
  // its own: the чан and the прядка each had two entries, one for the block
  // and one for the item that places it, and they are the same thing.
  const byItem = new Set<number>();
  for (const item of PLAIN_ITEMS) {
    const block = placedBlock(item.id);
    if (block !== 0) byItem.add(block);
  }

  const add = (id: ItemId): void => {
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ id, group: groupOf(id) });
  };

  for (const block of BLOCKS) {
    if (block.id === 0) continue;
    if (block.id >= FIRST_NON_BLOCK_ITEM) continue;
    if (!isPlaceable(block.id)) continue;
    /*
     * One entry per thing, not one per state.
     *
     * A door is thirty-two block ids and one item; a stove is sixteen and
     * one. Left in, those eighty-eight states buried everything else on the
     * shelf and the palette was unusable for the thing it exists for. They
     * are reached through their own items, which come round in PLAIN_ITEMS
     * below.
     */
    if (isDoor(block.id) || isShutter(block.id) || isBed(block.id) ||
        isStove(block.id) || isChest(block.id) || isRushnyk(block.id) ||
        isWall(block.id)) {
      continue;
    }
    /*
     * And every other state of something that is already here.
     *
     * "Прибери дублікати з комори." A требушет is four ids and one engine, a
     * хвіртка eight and one gate, сходи eight of the same timber once they
     * can hang upside down - and the shelf held every one of them.
     *
     * One rule instead of a predicate per family, and it is the rule the
     * world already states: **a block that breaks into a differently
     * numbered thing of the same name is a state of that thing.** The stair
     * facing drops the stair; the open gate drops the gate; the granite wall
     * drops the гранітна стіна you hold. The name is what makes it the same
     * *thing* - трава breaks into земля and those are two things, so grass
     * keeps its place on the shelf.
     */
    const drop = block.drops?.[0]?.item;
    if (drop !== undefined && drop !== block.id &&
      itemDef(drop).label === block.label) continue;
    // The креслярські камені are the turnable things whose item *is* a block
    // id, so each keeps exactly one of its four facings - the first. Asked of
    // the stone's own range rather than compared against the хата's, which
    // silently dropped the кремль stone off the shelf the day it was added.
    if (isBlueprint(block.id) && blueprintBase(block.id) !== block.id) continue;
    if (byItem.has(block.id)) continue;
    if (itemDef(block.id).name !== block.name) continue;
    add(block.id);
  }
  for (const item of PLAIN_ITEMS) add(item.id);

  const rank = new Map(GROUP_ORDER.map((g, i) => [g, i]));
  return out.sort((a, b) => {
    const byGroup = (rank.get(a.group) ?? 99) - (rank.get(b.group) ?? 99);
    return byGroup !== 0 ? byGroup : a.id - b.id;
  });
}
